import { ANALYTICS_EVENT_NAMES } from "analytics/catalog/events";
import { buildCommonEventProps } from "analytics/core/commonProps";
import { trackEvent } from "analytics/core/tracker";
import { CancellationReasonCategory } from "helpers/booking-cancellation-helper";

export type BookingCancellationEntryPoint =
  | "pending_activities"
  | "influencer_card"
  | "home_accepted_card";

export type BookingCancellationAnalyticsContext = {
  pathname: string;
  search: string;
  businessId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  planType?: string | null;
  currency?: string | null;
  bookingId: string;
  campaignId: string;
  restaurantId?: string | null;
  creatorId?: string | null;
  reason?: CancellationReasonCategory | null;
  timeBeforeEventMinutes?: number | null;
  isLastMinute?: boolean | null;
  entryPoint: BookingCancellationEntryPoint;
};

const buildBookingCancellationProps = (
  context: BookingCancellationAnalyticsContext,
): Record<string, unknown> => {
  const commonProps = buildCommonEventProps({
    screenKey: "booking_cancellation_modal",
    pathname: context.pathname,
    search: context.search,
    businessId: context.businessId ?? null,
    userId: context.userId ?? null,
    userEmail: context.userEmail ?? null,
    planType: context.planType ?? null,
    currency: context.currency ?? null,
    campaignId: context.campaignId ?? null,
  });

  return {
    ...commonProps,
    booking_id: context.bookingId,
    campaign_id: context.campaignId,
    restaurant_id: context.restaurantId ?? context.businessId ?? null,
    creator_id: context.creatorId ?? null,
    reason: context.reason ?? null,
    time_before_event_minutes: context.timeBeforeEventMinutes ?? null,
    is_last_minute:
      typeof context.isLastMinute === "boolean" ? context.isLastMinute : null,
    entry_point: context.entryPoint,
  };
};

export const trackBookingCancellationInitiated = (
  context: BookingCancellationAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.bookingCancellationInitiated,
    buildBookingCancellationProps(context),
  );
};

export const trackBookingCancellationReasonSelected = (
  context: BookingCancellationAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.bookingCancellationReasonSelected,
    buildBookingCancellationProps(context),
  );
};

export const trackBookingCancellationSubmitted = (
  context: BookingCancellationAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.bookingCancellationSubmitted,
    buildBookingCancellationProps(context),
  );
};

export const trackBookingCancellationAbandoned = (
  context: BookingCancellationAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.bookingCancellationAbandoned,
    buildBookingCancellationProps(context),
  );
};
